import * as React from 'react'
import { readsRtl, sigAnimate, signatureMoves } from '../../lib/signature/motion'
import { cn } from '../../lib/utils'
import {
  CELL,
  CYCLE,
  cellsIn,
  cycleStart,
  heightDelay,
  lineKeyframes,
  PITCH,
  span,
} from './kufic-clock'
import { useSkeletonReveal } from './skeleton-reveal'

/** A skeleton cell is 10px on a 14px pitch (DESIGN.md "Square Kufic in structure"). */
export { cellsIn } from './kufic-clock'

const cellsOf = (color: string, toward: 'right' | 'left') =>
  `repeating-linear-gradient(to bottom, transparent 0 10px, var(--card) 10px 14px), repeating-linear-gradient(to ${toward}, ${color} 0 10px, transparent 10px 14px)`

export interface KuficSkeletonProps extends React.ComponentProps<'div'> {
  /** The width of the text this line stands in for, as a CSS length (default: the whole line). */
  width?: string
  /** Retained for callers that used to identify a row; timing now follows page height. */
  row?: number
  /** One text row, or two cell rows for a tall figure. */
  rows?: 1 | 2
}

/**
 * A loading line in square Kufic: 10px cells on a 14px pitch, snapped to the
 * whole cells that fit the width it stands in for, so nothing shifts when the
 * text lands. While the data loads the cells write in reading order on a
 * height-based, phase-locked 2400ms loop, then clear together. Under reduced
 * motion the cells simply stand.
 */
function KuficSkeleton({
  width = '100%',
  row,
  rows = 1,
  className,
  style,
  ...props
}: KuficSkeletonProps) {
  const boxRef = React.useRef<HTMLDivElement>(null)
  const inkRef = React.useRef<HTMLSpanElement>(null)
  const [geometry, setGeometry] = React.useState<{ cells: number; delay: number } | null>(null)
  useSkeletonReveal(boxRef)

  React.useLayoutEffect(() => {
    const box = boxRef.current
    if (!box) return
    const measure = () => {
      const region =
        box.closest<HTMLElement>('[data-skeleton-region]') ?? box.closest<HTMLElement>('main')
      const bounds = box.getBoundingClientRect()
      const next = {
        cells: cellsIn(bounds.width),
        delay: heightDelay(bounds.top, region?.getBoundingClientRect().top ?? 0),
      }
      setGeometry((current) =>
        current?.cells === next.cells && current.delay === next.delay ? current : next,
      )
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    return () => observer.disconnect()
  }, [])

  React.useEffect(() => {
    const ink = inkRef.current
    if (!ink || !geometry || !signatureMoves()) return
    const rtl = readsRtl(ink)
    let animation: Animation | undefined
    let observer: IntersectionObserver | undefined
    const start = () => {
      if (animation) return
      animation = sigAnimate(ink, lineKeyframes(geometry.cells, geometry.delay, rtl), {
        duration: CYCLE,
        iterations: Number.POSITIVE_INFINITY,
      })
      animation.startTime = cycleStart((document.timeline?.currentTime as number | null) ?? 0)
    }
    const lineTop = ink.getBoundingClientRect().top
    if (lineTop > window.innerHeight + 200) {
      if (typeof IntersectionObserver !== 'undefined') {
        observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              observer?.disconnect()
              start()
            }
          },
          { rootMargin: '0px 0px 200px 0px' },
        )
        observer.observe(ink)
      }
    } else start()

    return () => {
      observer?.disconnect()
      animation?.cancel()
    }
  }, [geometry])

  const cells = geometry?.cells ?? null
  const line = cells === null ? '100%' : `${span(cells)}px`

  return (
    <div
      ref={boxRef}
      data-slot="kufic-skeleton"
      data-row={row}
      data-cells={cells ?? undefined}
      data-delay-ms={geometry?.delay}
      aria-hidden="true"
      className={cn('relative max-w-full', rows === 2 ? 'h-6' : 'h-2.5', className)}
      style={{ width, height: rows === 2 ? '24px' : '10px', ...style }}
      {...props}
    >
      <div
        className="absolute inset-y-0 start-0 overflow-hidden [background-image:var(--cells-ltr)] rtl:[background-image:var(--cells-rtl)]"
        style={
          {
            width: line,
            '--cells-ltr': cellsOf('var(--skeleton)', 'right'),
            '--cells-rtl': cellsOf('var(--skeleton)', 'left'),
          } as React.CSSProperties
        }
      >
        <span
          ref={inkRef}
          className="absolute inset-y-0 start-0 [background-image:var(--cells-ltr)] [transform:translateX(-100%)] rtl:[background-image:var(--cells-rtl)] rtl:[transform:translateX(100%)]"
          style={
            {
              // One trailing gap wider, so the layer is whole pitches long and
              // every step of the slide lands on a cell.
              width: `calc(100% + ${PITCH - CELL}px)`,
              '--cells-ltr': cellsOf('var(--skeleton-ink)', 'right'),
              '--cells-rtl': cellsOf('var(--skeleton-ink)', 'left'),
            } as React.CSSProperties
          }
        />
      </div>
    </div>
  )
}

export { KuficSkeleton }
